import { useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Flex,
  Icon,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { MdCalendarToday } from 'react-icons/md';
import CustomDrawer from 'components/ui/Drawer';
import AppointmentCompleteModal from 'components/layouts/patients/AppointmentCompleteModal';
import AppointmentCancelModal from 'components/layouts/patients/AppointmentCancelModal';
import AppointmentRescheduleModal from 'components/layouts/patients/AppointmentRescheduleModal';
import { useAppointments } from 'hooks/useAppointments';
import { useCurrentDbUser } from 'hooks/useCurrentUser';
import { Appointment, EstadoCita } from 'types/models';
import {
  AppointmentListContent,
  todayRange,
} from './TodayJornada';

type ModalType = 'complete' | 'reschedule' | 'cancel' | null;

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export default function TodayQuickDrawer({ isOpen, onClose }: Props) {
  const textColor  = useColorModeValue('secondaryGray.900', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');

  const { data: me } = useCurrentDbUser();
  const range = useMemo(todayRange, []);

  const { data, isLoading, isError } = useAppointments(
    me?.id ? { desde: range.desde, hasta: range.hasta, professionalId: me.id, limit: 50 } : {}
  );

  const appointments: Appointment[] = useMemo(() => {
    if (!data?.data) return [];
    return [...data.data].sort(
      (a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime()
    );
  }, [data]);

  const [selected,  setSelected]  = useState<Appointment | null>(null);
  const [modalType, setModalType] = useState<ModalType>(null);

  const handleAction = (appt: Appointment, type: ModalType) => {
    setSelected(appt);
    setModalType(type);
  };

  const handleModalClose = () => {
    setSelected(null);
    setModalType(null);
  };

  const total      = appointments.length;
  const pendientes = appointments.filter(a => a.estado === EstadoCita.CONFIRMADA).length;

  const dateLabel = new Date().toLocaleDateString('es-EC', {
    weekday: 'long', day: 'numeric', month: 'long',
  });

  const drawerTitle = (
    <Flex align='center' gap='10px' w='100%'>
      <Icon as={MdCalendarToday} w='20px' h='20px' color='brand.500' />
      <Box>
        <Text fontSize='md' fontWeight='700' color={textColor} lineHeight='1.2'>
          Citas de Hoy
        </Text>
        <Text fontSize='xs' color={mutedColor} textTransform='capitalize'>
          {dateLabel}
        </Text>
      </Box>
      {!isLoading && total > 0 && (
        <Flex gap='6px' ml='auto' mr='32px'>
          <Badge colorScheme='brand' borderRadius='full' px='8px' fontSize='xs'>
            {total} cita{total !== 1 ? 's' : ''}
          </Badge>
          {pendientes > 0 && (
            <Badge colorScheme='orange' borderRadius='full' px='8px' fontSize='xs'>
              {pendientes} pendiente{pendientes !== 1 ? 's' : ''}
            </Badge>
          )}
        </Flex>
      )}
    </Flex>
  );

  return (
    <>
      <CustomDrawer
        isOpen={isOpen}
        onClose={onClose}
        placement='right'
        width='580px'
        title={drawerTitle}
        contentPaddingX='20px'
        contentPaddingY='16px'
        borderRadius='10px'
        borderRadiusSides='left'
      >
        <AppointmentListContent
          appointments={appointments}
          isLoading={isLoading}
          isError={isError}
          onAction={handleAction}
        />
      </CustomDrawer>

      {selected && modalType === 'complete' && (
        <AppointmentCompleteModal isOpen onClose={handleModalClose} appointment={selected} />
      )}
      {selected && modalType === 'reschedule' && (
        <AppointmentRescheduleModal isOpen onClose={handleModalClose} appointment={selected} />
      )}
      {selected && modalType === 'cancel' && (
        <AppointmentCancelModal isOpen onClose={handleModalClose} appointment={selected} />
      )}
    </>
  );
}
